import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import {
  BenefitDTOBenefitEnum,
  CouponsV2BenefitOptionsDTO,
  CreateBenefitActivationDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { apis, useTenantQuery } from 'probonio-shared-ui/module/api';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SelectControl } from '../../../../component/form';
import { MAX_NON_CASH_MONTHLY_BUDGET_CENTS } from '../../../../constants';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import { fillNumberRangeArray } from '../../../../util/numberRange';
import { ActivationDialogExtensions } from '../../ActivationDialog';
import { NonCashLimitWarning } from '../../ActivationDialog/NonCashLimitWarning';
import { AvailableCouponsAccordion } from '../../coupons/AvailableCouponsDrawer';

interface CouponsV2BenefitOptions {
  monthlyBudget: number;
}

// TODO this value should be configurable per tax period (TaxConfig)
const MIN = 500;
const BUDGET_OPTIONS = fillNumberRangeArray(MIN, MAX_NON_CASH_MONTHLY_BUDGET_CENTS, 500);
BUDGET_OPTIONS.push(4400);
BUDGET_OPTIONS.sort((a, b) => a - b);

const Header: React.FC = () => {
  const { watch } = useFormContext();
  const { employeeId } = useParams();

  const benefitOptions: CouponsV2BenefitOptionsDTO = watch('benefitOptions');

  return (
    <>
      <NonCashLimitWarning employeeId={employeeId} benefit={BenefitDTOBenefitEnum.CouponsV2} monthlyBudget={benefitOptions.monthlyBudget} />
    </>
  );
};

const Fields: React.FC = () => {
  const { watch } = useFormContext();

  const benefitOptions: CouponsV2BenefitOptionsDTO = watch('benefitOptions');

  const { data, isLoading } = useTenantQuery(['benefits', 'coupons', 'definition', apis.coupons.findCouponDefinitions.name], tenantId =>
    apis.coupons.findCouponDefinitions({ tenantId }).then(resp => resp.data),
  );

  return (
    <>
      {!isLoading && benefitOptions.monthlyBudget && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <AvailableCouponsAccordion coupons={data?.coupons} amount={benefitOptions.monthlyBudget} />
        </Box>
      )}
    </>
  );
};

const InlineField: React.FC = () => {
  const { t, i18n } = useTranslation('benefitsModule');
  const { control } = useFormContext();

  return (
    <SelectControl
      dataTestId="monthlyBudgetValue"
      control={control}
      name="benefitOptions.monthlyBudget"
      label={t('COUPONS_V2.fields.monthlyBudget')}
      fullWidth
      margin="dense"
      rules={{ required: true }}
      options={BUDGET_OPTIONS.map(n => ({ value: n, label: i18n.format(n, 'euro-float') }))}
    />
  );
};

const resetCouponsV2BenefitActivationOptions: ActivationDialogExtensions<CouponsV2BenefitOptions>['onResetBenefitOptions'] =
  selectedActivation => {
    return {
      monthlyBudget: selectedActivation?.couponsV2Options
        ? selectedActivation.couponsV2Options.monthlyBudget
        : BUDGET_OPTIONS[BUDGET_OPTIONS.length - 1],
    };
  };

const mapCouponsV2Options = (benefitOptions: CouponsV2BenefitOptions) => ({
  monthlyBudget: benefitOptions.monthlyBudget,
});

const createCouponsV2BenefitActivation: ActivationDialogExtensions<CouponsV2BenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.CouponsV2,
    couponsV2Options: mapCouponsV2Options(benefitOptions),
  };
  await saveActivation(create);
};

const updateCouponsV2BenefitActivation: ActivationDialogExtensions<CouponsV2BenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    couponsV2Options: mapCouponsV2Options(benefitOptions),
  };
  await saveActivation(update);
};

export class CouponsV2BenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<CouponsV2BenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);
  Header?: React.ComponentType = Header;
  InlineField?: React.ComponentType = InlineField;
  createLabel?: string = 'common:buttons.orderWithPayment';
  earliestStartDate?: () => DateTime = () => DateTime.now().plus({ months: DateTime.now().day > 15 ? 1 : 0 });

  onCreateActivation = createCouponsV2BenefitActivation;
  onUpdateActivation = updateCouponsV2BenefitActivation;
  onResetBenefitOptions? = resetCouponsV2BenefitActivationOptions;
}
